import { KPI } from '@/components/ui/KPI';
import { SectionHead } from '@/components/ui/SectionHead';
import { SeriesBars } from '@/components/SeriesBars';
import { GamesByBrandTable } from '@/components/GamesByBrandTable';
import { SegmentTable } from '@/components/SegmentTable';
import { ChartCard } from '@/components/ui/ChartCard';
import { StackedDailyBars } from '@/components/StackedDailyBars';
import { SourceSplitDonut } from '@/components/SourceSplitDonut';
import { sourceColor } from '@/components/SourceSplitDonut/colors';
import type { GamesMissionsSection, OverviewSegment } from '@/api/worldCup';
import type { DashboardConfig } from '../config';

type Props = {
  section: GamesMissionsSection;
  brandColors: Record<string, string>;
  /** Per-brand active-weeks string (e.g. "W22-W25") keyed by brand_id. */
  brandWeeks?: Record<string, string>;
  /** Backend per-brand performance rows — may be undefined when the region ships none. */
  segments?: OverviewSegment[];
  /** Synthesised rows from `campaign.brands`, used when `segments` is empty. */
  fallbackSegments?: OverviewSegment[];
  hide?: DashboardConfig['hide'];
};

export function GamesMissionsView({ section, brandColors, brandWeeks = {}, segments, fallbackSegments = [], hide = {} }: Props) {
  const brandRows = segments && segments.length > 0 ? segments : fallbackSegments;

  // How plays were funded (free daily allowance vs earned by spending). When
  // the payload carries the per-day split, the games bar chart becomes stacked
  // by source instead of a single total per day.
  const bySource = section.games_by_source;
  const bySourceDay = section.games_by_source_day;
  const sourceKeys = (bySourceDay?.brands ?? []).map((b, i) => ({ key: b.brand_id, label: b.name, color: sourceColor(b.brand_id, i) }));

  return (
    <div>
      <SectionHead tag="games & missions">Gameplay overview</SectionHead>
      <div className="grid-2">
        <KPI label="Total games" value={section.kpis.total_games.display} accent="engage" foot={section.kpis.total_games.foot} />
        <KPI label="Avg games / user" value={section.kpis.avg_games_per_user.display} accent="engage" foot={section.kpis.avg_games_per_user.foot} />
      </div>

      {/* Free vs purchased proportion — only where the pipeline ships it. */}
      {bySource && bySource.slices.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <ChartCard title={bySource.label}>
            <SourceSplitDonut slices={bySource.slices} total={bySource.total} />
          </ChartCard>
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        {bySourceDay && sourceKeys.length > 0 ? (
          <ChartCard title={bySourceDay.label} unit={bySourceDay.unit}>
            <StackedDailyBars data={bySourceDay} keys={sourceKeys} />
          </ChartCard>
        ) : (
          <SeriesBars weekly={section.weekly_games_played} daily={section.daily_games_played} />
        )}
      </div>

      {/* Distinct players per day, stacked by brand — Spain. */}
      {section.players_by_day && section.players_by_day.brands.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <ChartCard title={section.players_by_day.label} unit={section.players_by_day.unit}>
            <StackedDailyBars data={section.players_by_day} keys={section.players_by_day.brands.map((b) => ({ key: b.brand_id, label: b.name }))} />
          </ChartCard>
        </div>
      )}

      {/* Games-by-brand stats — shown when the backend ships rows AND the
          region hasn't hidden it (Brazil hides it: its Overview per-brand
          table already covers the breakdown). */}
      {!hide.gamesByBrand && section.games_by_brand.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <GamesByBrandTable rows={section.games_by_brand} brandColors={brandColors} brandWeeks={brandWeeks} />
        </div>
      )}

      {/* Brand roster + per-brand metrics — moved here from the Overview tab. */}
      {brandRows.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <SegmentTable segments={brandRows} brandColors={brandColors} hide={hide} />
        </div>
      )}
    </div>
  );
}
